/**
 * TagPicker — F031
 *
 * Multi-Select Component for assigning tags to Hotspots or Contacts.
 * Used inline on Hotspot/Contact detail pages.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  useTags,
  useAddHotspotsToTag,
  useAddContactsToTag,
  useRemoveHotspotsFromTag,
  useRemoveContactsFromTag,
} from './useTags'

type Props = {
  /** 'hotspot' or 'contact' */
  targetType: 'hotspot' | 'contact'
  /** The ID of the hotspot or contact */
  targetId: number
  /** IDs of currently assigned tags */
  assignedTagIds: number[]
  /** Callback when assignments change */
  onChange?: (newTagIds: number[]) => void
}

export function TagPicker({ targetType, targetId, assignedTagIds, onChange }: Props) {
  const { t } = useTranslation('tags')
  const { data: allTags, isLoading } = useTags()
  const addHotspots = useAddHotspotsToTag()
  const addContacts = useAddContactsToTag()
  const removeHotspots = useRemoveHotspotsFromTag()
  const removeContacts = useRemoveContactsFromTag()

  const [isOpen, setIsOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>(assignedTagIds)

  // BUGFIX #43: die Auswahl war reiner Initial-State und lief nach dem
  // Speichern gegen den alten Stand weiter (useTags invalidiert, die Props
  // aktualisieren sich, die lokale Auswahl blieb stehen). Bei jeder
  // Server-Aenderung neu synchronisieren — als Render-Anpassung statt
  // setState-in-Effect (react-hooks/set-state-in-effect), siehe
  // https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
  const assignedKey = [...assignedTagIds].sort((a, b) => a - b).join(',')
  const [prevAssignedKey, setPrevAssignedKey] = useState(assignedKey)
  if (assignedKey !== prevAssignedKey) {
    setPrevAssignedKey(assignedKey)
    setSelectedIds(assignedTagIds.slice())
  }

  const assignedTags = allTags?.filter(t => assignedTagIds.includes(t.id)) ?? []
  const unassignedTags = allTags?.filter(t => !assignedTagIds.includes(t.id)) ?? []

  const hasPendingChanges =
    selectedIds.some(id => !assignedTagIds.includes(id)) ||
    assignedTagIds.some(id => !selectedIds.includes(id))

  function toggleTag(tagId: number) {
    const newIds = selectedIds.includes(tagId)
      ? selectedIds.filter(id => id !== tagId)
      : [...selectedIds, tagId]
    setSelectedIds(newIds)
  }

  async function handleRemoveTag(tagId: number) {
    // Sofort-Entfernen: auch die lokale Auswahl mitziehen, sonst wuerde ein
    // anschliessendes Speichern den Tag wieder anlegen (#43).
    setSelectedIds(ids => ids.filter(id => id !== tagId))
    if (targetType === 'hotspot') {
      await removeHotspots.mutateAsync({ tagId, hotspotIds: [targetId] })
    } else {
      await removeContacts.mutateAsync({ tagId, contactIds: [targetId] })
    }
    onChange?.(assignedTagIds.filter(id => id !== tagId))
  }

  async function handleSave() {
    const toAdd = selectedIds.filter(id => !assignedTagIds.includes(id))
    // BUGFIX #43: abgewaehlte Tags wurden beim Speichern verworfen — die
    // Zuordnung blieb serverseitig bestehen und die Anzeige sprang nach der
    // Invalidierung auf den alten Stand zurueck.
    const toRemove = assignedTagIds.filter(id => !selectedIds.includes(id))

    for (const tagId of toAdd) {
      if (targetType === 'hotspot') {
        await addHotspots.mutateAsync({ tagId, hotspotIds: [targetId] })
      } else {
        await addContacts.mutateAsync({ tagId, contactIds: [targetId] })
      }
    }

    for (const tagId of toRemove) {
      if (targetType === 'hotspot') {
        await removeHotspots.mutateAsync({ tagId, hotspotIds: [targetId] })
      } else {
        await removeContacts.mutateAsync({ tagId, contactIds: [targetId] })
      }
    }

    onChange?.(selectedIds)
    setIsOpen(false)
  }

  return (
    <div className="tag-picker" data-testid="tag-picker">
      {/* Current Tags Display */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        {assignedTags.map(tag => (
          <span
            key={tag.id}
            className="badge"
            style={{ padding: '4px 8px', fontSize: 12 }}
          >
            {tag.name}
          </span>
        ))}
        {assignedTags.length === 0 && (
          <span className="muted" style={{ fontSize: 12 }}>{t('picker.noTagsAssigned')}</span>
        )}
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setIsOpen(!isOpen)}
          style={{ marginLeft: 'auto' }}
          data-testid="tag-picker-toggle"
        >
          {isOpen ? t('picker.toggleClose') : t('picker.toggleEdit')}
        </button>
      </div>

      {/* Tag Selection Panel */}
      {isOpen && (
        <div className="card card-pad" style={{ marginTop: 12 }}>
          <h4 style={{ marginBottom: 12 }}>{t('picker.panelTitle')}</h4>

          {isLoading && <p className="muted">{t('loading.inline')}</p>}

          {!isLoading && unassignedTags.length === 0 && assignedTags.length === allTags?.length && (
            <p className="muted" style={{ fontSize: 13 }}>{t('picker.allAssigned')}</p>
          )}

          {/* Assigned Tags - can be removed */}
          {!isLoading && assignedTags.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 8 }}>
                {t('picker.sectionAssigned')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {assignedTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    className="btn btn-sm badge badge-primary"
                    onClick={() => handleRemoveTag(tag.id)}
                    disabled={removeHotspots.isPending || removeContacts.isPending}
                    data-testid={`tag-remove-${tag.id}`}
                  >
                    {tag.name} ×
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Unassigned Tags - can be added */}
          {!isLoading && unassignedTags.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 8 }}>
                {t('picker.sectionAvailable')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {unassignedTags.map(tag => (
                  <button
                    key={tag.id}
                    type="button"
                    className="btn btn-sm"
                    onClick={() => toggleTag(tag.id)}
                    data-testid={`tag-add-${tag.id}`}
                  >
                    + {tag.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Currently Selected (to be added) */}
          {selectedIds.filter(id => !assignedTagIds.includes(id)).length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, color: 'var(--color-muted)', marginBottom: 8 }}>
                {t('picker.sectionSelected')}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {selectedIds
                  .filter(id => !assignedTagIds.includes(id))
                  .map(id => {
                    const tag = allTags?.find(t => t.id === id)
                    return tag ? (
                      <span
                        key={id}
                        className="badge badge-primary"
                        style={{ padding: '4px 8px', fontSize: 12, cursor: 'pointer' }}
                        onClick={() => toggleTag(id)}
                        data-testid={`tag-selected-${id}`}
                      >
                        {tag.name} ×
                      </span>
                    ) : null
                  })}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => {
                setSelectedIds(assignedTagIds)
                setIsOpen(false)
              }}
              disabled={addHotspots.isPending || addContacts.isPending || removeHotspots.isPending || removeContacts.isPending}
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={handleSave}
              disabled={
                addHotspots.isPending ||
                addContacts.isPending ||
                removeHotspots.isPending ||
                removeContacts.isPending ||
                !hasPendingChanges
              }
              data-testid="tag-picker-save"
            >
              {(addHotspots.isPending || addContacts.isPending) ? t('picker.savingLabel') : t('common:actions.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
